import * as React from 'react';
import dayjs from 'dayjs';
import {
    Box,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import RepeatIcon from '@mui/icons-material/Repeat';
import SearchIcon from '@mui/icons-material/Search';
import {useQuery} from '@tanstack/react-query';
import {searchTasksQuery} from '../../services/taskService';
import {TaskSearchResult} from '../../types/task';
import {queryKeys} from '../../data/queryKeys';
import {LIMITS} from '../../contract';

type TaskSearchProps = {
    boardId: string;
    sectionTitles: Record<string, string>;
    // Called with the chosen result; the results are dismissed afterwards.
    onSelect: (result: TaskSearchResult) => void;
};

// Search box with a dropdown of matching tasks (full-text search over titles; a recurring
// series appears once, as its occurrence nearest to today).
export function TaskSearch({boardId, sectionTitles, onSelect}: TaskSearchProps) {
    const [searchText, setSearchText] = React.useState('');
    const [debouncedSearch, setDebouncedSearch] = React.useState('');

    React.useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
        return () => clearTimeout(timer);
    }, [searchText]);

    const {data: searchResults, isFetching: isSearching, isError: searchFailed} = useQuery({
        queryKey: queryKeys.taskSearch(boardId, debouncedSearch),
        queryFn: () => searchTasksQuery(boardId, debouncedSearch),
        enabled: debouncedSearch.length > 0,
        staleTime: 1000 * 30,
    });

    function clearSearch() {
        setSearchText('');
        setDebouncedSearch('');
    }

    function select(result: TaskSearchResult) {
        onSelect(result);
        clearSearch();
    }

    return (
        <Box sx={{ position: 'relative', width: '100%', maxWidth: 420 }}>
            <TextField
                fullWidth
                size="small"
                placeholder="Search tasks…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                slotProps={{
                    htmlInput: { 'aria-label': 'Search tasks', maxLength: LIMITS.searchQuery },
                    input: {
                        startAdornment: (
                            <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>
                        ),
                        endAdornment: searchText ? (
                            <InputAdornment position="end">
                                <IconButton size="small" aria-label="Clear search" onClick={clearSearch}>
                                    <ClearIcon fontSize="small"/>
                                </IconButton>
                            </InputAdornment>
                        ) : undefined,
                    },
                }}
            />

            {debouncedSearch && (
                // Floats over the content below instead of pushing it down.
                <Paper variant="outlined" sx={{ position: 'absolute', left: 0, right: 0, top: '100%', mt: 0.5, zIndex: 10, boxShadow: 3, maxHeight: 300, overflowY: 'auto' }}>
                    {searchFailed ? (
                        <Typography variant="body2" color="error" sx={{ p: 1.5 }}>Search failed. Try again.</Typography>
                    ) : isSearching && !searchResults ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>Searching…</Typography>
                    ) : searchResults?.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>No matching tasks.</Typography>
                    ) : (
                        <List dense disablePadding>
                            {searchResults?.map((result) => (
                                <ListItemButton key={result.id} onClick={() => select(result)}>
                                    <ListItemText
                                        primary={
                                            <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                                                <span>{result.title}</span>
                                                {result.occurrences > 1 && (
                                                    <RepeatIcon fontSize="inherit" color="action" titleAccess="Recurring task"/>
                                                )}
                                            </Stack>
                                        }
                                        secondary={[
                                            result.date ? dayjs(result.date).format('ddd, MMM D · h:mm A') : 'No date',
                                            sectionTitles[result.sectionId],
                                            result.occurrences > 1 ? `${result.occurrences} occurrences, nearest shown` : undefined,
                                        ].filter(Boolean).join(' · ')}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </Paper>
            )}
        </Box>
    );
}
